import { inject, Injectable } from '@angular/core';
import { ApiService } from '@libs/apis';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { GeoTestDetails } from '../models/GeoTestDetails';
import { first } from 'lodash-es';

export enum MimStatus {
  ELIGIBLE = 'eligible',
  INELIGIBLE = 'ineligible',
  IN_PROGRESS = 'in_progress'
}

interface CalibrationStatus {
  mimStatus: MimStatus;
  optInStatus: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TestCalibrationComparisonService {
  #selectionService = inject(SelectionService);
  brandSlug = this.#selectionService.buildSelectionSlug();
  #apiService = inject(ApiService);

  private calibrationStatusMap = new Map<string, CalibrationStatus>();

  private getStatusKey(studyId: number, conversionType: string): string {
    return `${studyId}-${conversionType}`;
  }

  async fetchCalibrationStatusForStudy(
    studyId: number,
    clientId: number,
    brandId: number,
    conversionType: string
  ): Promise<CalibrationStatus> {
    const statusKey = this.getStatusKey(studyId, conversionType);

    if (this.calibrationStatusMap.has(statusKey)) {
      return this.calibrationStatusMap.get(statusKey)!;
    }

    const response =
      await this.#apiService.platformApisOperations.getTestCalibrationStatusForStudy(
        {
          clientId,
          brandId,
          conversionType,
          studyId
        }
      );
    const firstValue = first(response.table.values);
    const calibrationStatus = {
      mimStatus: firstValue?.status.value as unknown as MimStatus,
      optInStatus: firstValue?.include.value as unknown as boolean
    };

    this.calibrationStatusMap.set(statusKey, calibrationStatus);

    return calibrationStatus;
  }

  async getTestCalibrationStatus(
    studyDetails: GeoTestDetails
  ): Promise<CalibrationStatus> {
    const studyId = studyDetails.geoStudy?.studyId;
    const clientId = studyDetails.geoStudy?.clientId;
    const brandId = studyDetails.geoStudy?.brandId;

    if (!studyId || !clientId || !brandId) {
      return Promise.resolve({
        mimStatus: MimStatus.IN_PROGRESS,
        optInStatus: false
      });
    }

    return this.fetchCalibrationStatusForStudy(
      studyId,
      clientId,
      brandId,
      studyDetails.geoStudy?.conversionType ?? ''
    );
  }

  async preloadCalibrationForStudies(
    studyDetailsArray: {
      studyId: number;
      cellConfigId: number;
      clientId: number;
      brandId: number;
      conversionType: string;
    }[]
  ): Promise<void> {
    if (studyDetailsArray.length === 0) {
      return Promise.resolve();
    }

    const promises = studyDetailsArray.map(
      ({ studyId, clientId, brandId, conversionType }) =>
        this.fetchCalibrationStatusForStudy(
          studyId,
          clientId,
          brandId,
          conversionType
        ).catch((error) => {
          console.error(
            'Error pre-loading calibration for study:',
            studyId,
            error
          );
          return { mimStatus: MimStatus.IN_PROGRESS, optInStatus: false };
        })
    );

    await Promise.all(promises);
  }

  clearCalibrationStatusMap(): void {
    this.calibrationStatusMap.clear();
  }

  clearCalibrationStatusForStudy(
    studyId: number,
    conversionType: string
  ): void {
    const statusKey = this.getStatusKey(studyId, conversionType);
    this.calibrationStatusMap.delete(statusKey);
  }
}
